<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin session-activities-dialog">
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ $t('sessions.modals.changeSessionActivity.textTitle') }}
        </div>
        <div class="row items-center q-col-gutter-sm">
          <div>
            <q-btn
              v-close-popup
              flat
              round
              dense
              icon="close"
              class="col-auto"
            />
          </div>
        </div>
      </q-card-section>
      <q-card-section class="row">
        <session-activities-table
          v-model:selected="selectedSessionActivities"
          flat
          selection="single"
          :default-selection="[sessionActivityId]"
          :disable-check="isSessionActivityDisabled"
          :disabled-selection-tooltip="$t('sessions.modals.changeSessionActivity.tooltipAlreadyLinked')"
          :fixed-filters="fixedFilters"
          row-select
          disable-views
          hide-bulk-actions
          class="full-width"
        />
      </q-card-section>
      <q-card-actions class="row">
        <q-btn
          v-if="!!sessionActivityId && allowUnlink"
          color="negative"
          :label="$t('run.overview.appointmentUnlink')"
          flat
          @click="onUnlinkClick"
        />
        <q-space />
        <q-btn
          color="secondary"
          :label="$t('common.buttonCancel')"
          flat
          @click="onCancelClick"
        />
        <q-btn
          color="primary"
          :loading="showSpinner"
          :label="$t('common.buttonSave')"
          :disable="!canSubmit"
          flat
          data-e2e="button-save"
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { filterPropertyTypes } from 'boot/globals';
import SessionActivitiesTable from 'components/Sessions/SessionActivitiesTable';
import { Timestamp } from 'firebase/firestore';
import { chain } from 'lodash';
import { mapState } from 'pinia';
import { uid } from 'quasar';
import { useTime } from 'src/composables/time';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';

export default {
  name: 'ChangeAppointmentSessionActivity',

  components: { SessionActivitiesTable },

  mixins: [Integrations, Interactions],

  props: {

    sessionActivityId: {
      type: String,
      required: true,
    },

    clientId: {
      type: String,
      default: null,
    },

    userId: {
      type: String,
      default: null,
    },

    startDate: {
      type: Date,
      required: true,
    },

    allowUnlink: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { getLocaleDateString, adjustDate } = useTime();
    return { getLocaleDateString, adjustDate };
  },

  data() {
    const startTimestamp = Timestamp.fromDate(
      this.adjustDate(this.startDate, { hour: 0, minute: 0, second: 0, millisecond: 0 }),
    );
    return {
      showSpinner: false,

      selectedSessionActivities: [],
      startTimestamp,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'connectedClinic']),
    ...mapState(clients, ['clients']),

    canSubmit() {
      return this.selectedSessionActivityId && (this.selectedSessionActivityId !== this.sessionActivityId);
    }, // canSubmit()

    selectedSessionActivityId() {
      return chain(this.selectedSessionActivities)
        .head()
        .get('id')
        .value();
    },

    fixedFilters() {
      const defaultDateRange = 'custom-dates';
      const label = this.getLocaleDateString(this.startTimestamp);

      return {
        startTimestamp: {
          id: uid(),
          filterProperty: 'startTimestamp',
          filterPropertyType: filterPropertyTypes.Date,
          isPersistent: true,
          filterStatements: [
            {
              fromDate: this.startTimestamp,
              toDate: Timestamp.fromDate(
                this.adjustDate(this.startDate, { hour: 23, minute: 59, second: 59, millisecond: 999 }),
              ),
              filterDateItems: [this.startTimestamp],
              filterCriteriaType: defaultDateRange,
              customDataId: null,
              filterNegative: false,
              filteredItems: [],
              label,
            },
          ],
        },
        client: {
          id: uid(),
          filterProperty: 'client',
          filterPropertyType: filterPropertyTypes.Client,
          filterStatements: [
            {
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: [this.clientId],
              label: null,
            },
          ],
        },
        userName: {
          id: uid(),
          filterProperty: 'user',
          filterPropertyType: filterPropertyTypes.User,
          filterStatements: [
            {
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: [this.userId],
              label: this.userDetails?.name,
            },
          ],
        },
      };
    }, // fixedFilters()

  },

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onOKClick() {
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      this.$emit('ok', this.selectedSessionActivityId);
      // or with payload: this.$emit('ok', { ... })

      // then hiding dialog
      this.hide();
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onUnlinkClick() {
      const confirmMessage = this.connectedClinic.integration
        ? this.$t('run.overview.confirmUnlinkAppointment', {
          integrationLabel: this.getIntegrationName(this.connectedClinic.integration),
        })
        : this.$t('run.overview.confirmUnlinkAppointmentNoIntegration');
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        confirmMessage,
        () => {
          this.$emit('ok', null);
          this.hide();
        },
      );
    }, // onUnlinkClick()

    isSessionActivityDisabled(sessionActivity) {
      return sessionActivity.appointment
          && (sessionActivity.id !== this.sessionActivityId);
    },

  },
};
</script>
<style scoped>
.session-activities-dialog {
  min-width:80vw;
}
</style>
